import useSWR from "swr";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import type { LinkedBoard } from "../../components/ItemViewSection/table.data.interface";

export interface ItemLocationResponse {
  id: string | number;
  board?: { id: string | number; name?: string };
  group?: { id: string; title: string };
}

/** Where a linked item currently lives, according to Monday. */
export interface LiveItemLocation {
  boardId: string;
  boardName?: string;
  groupName?: string;
}

/**
 * Keys locations by itemId alone. Deliberately NOT by boardId: a cross-board
 * move changes the board but preserves the item id, so boardId is exactly the
 * value we cannot trust as a lookup key.
 */
export function buildLocationMap(items: ItemLocationResponse[]): Record<string, LiveItemLocation> {
  const map: Record<string, LiveItemLocation> = {};
  for (const item of items ?? []) {
    const itemId = item?.id != null ? String(item.id) : "";
    const boardId = item?.board?.id != null ? String(item.board.id) : "";
    if (!itemId || !boardId) continue;
    map[itemId] = {
      boardId,
      boardName: item.board?.name,
      groupName: item.group?.title,
    };
  }
  return map;
}

async function fetchLinkedBoardLocations(
  linkedBoards: LinkedBoard[],
): Promise<Record<string, LiveItemLocation>> {
  if (!linkedBoards?.length) return {};
  const itemIds = linkedBoards.map((b) => parseInt(b.itemId, 10)).filter((id) => !Number.isNaN(id));
  const uniqueIds = [...new Set(itemIds)];
  if (!uniqueIds.length) return {};
  const items = await MondayApiQueryInstance.getItemCurrentLocations(uniqueIds);
  return buildLocationMap(items as ItemLocationResponse[]);
}

/** Stable default so consumers memoising on the map don't recompute every render. */
const EMPTY_LOCATIONS: Record<string, LiveItemLocation> = {};

/**
 * Resolves the current board + group for each linked item from Monday, because
 * SIMB receives no event when an item is moved to a different board and the
 * persisted record keeps pointing at the old one.
 *
 * revalidateOnFocus is enabled on purpose: moving an item happens on the Monday
 * board, so focus returning to this panel is the moment the cache is most
 * likely to be wrong.
 */
export function useLinkedBoardLocations(linkedBoards: LinkedBoard[] | null | undefined): {
  locationByItemId: Record<string, LiveItemLocation>;
  isLoading: boolean;
  error: unknown;
} {
  const itemIdKey = linkedBoards?.length
    ? [...new Set(linkedBoards.map((b) => b.itemId))].sort().join(",")
    : "";

  const {
    data: locationByItemId = EMPTY_LOCATIONS,
    isLoading,
    error,
  } = useSWR<Record<string, LiveItemLocation>>(
    itemIdKey ? `linkedBoardLocations:${itemIdKey}` : null,
    () => fetchLinkedBoardLocations(linkedBoards!),
    { revalidateOnFocus: true },
  );

  return { locationByItemId, isLoading, error };
}
